import { useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchUser } from "../../store/common/authSlice";
import {
  SmileUnsavedChangesProvider,
  useSmileUnsavedChanges,
} from "../../context/smileUnsavedChangesContext";

interface Props {
  allowedRoles: string[];
  allocationRoles?: string[];
}

function SmileCheckAuthContent({ allowedRoles }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { isAuth, user, isLoading } = useAppSelector((state) => state.auth);
  const { hasUnsavedChanges, setHasUnsavedChanges, runSaveHandler } =
    useSmileUnsavedChanges();

  const [initialized, setInitialized] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [savingAndLeaving, setSavingAndLeaving] = useState(false);
  const [pendingTargetUrl, setPendingTargetUrl] = useState<string | null>(null);
  const allowNavigationRef = useRef(false);

  const isGenerateRoute = location.pathname.startsWith("/dashboard/smiledesign/generate");
  const shouldGuardNavigation =
    hasUnsavedChanges && isGenerateRoute && !allowNavigationRef.current;

  useEffect(() => {
    let isMounted = true;

    if (!isAuth) {
      dispatch(fetchUser()).finally(() => {
        if (isMounted) setInitialized(true);
      });
    } else {
      setInitialized(true);
    }

    return () => {
      isMounted = false;
    };
  }, [dispatch, isAuth]);

  // Show warning modal if verified but has no credits
  useEffect(() => {
    const isGenerateRoute =
      location.pathname === "/dashboard/smiledesign/generate";

    if (
      initialized &&
      isAuth &&
      user &&
      user.role !== "Super Admin" &&
      user.verifySmile &&
      (user.smile_credits ?? 0) <= 0 &&
      isGenerateRoute
    ) {
      setShowCreditModal(true);
    } else {
      setShowCreditModal(false);
    }
  }, [initialized, isAuth, user, location.pathname]);

  useEffect(() => {
    if (!isGenerateRoute) {
      allowNavigationRef.current = false;
    }
  }, [isGenerateRoute]);

  useEffect(() => {
    if (!shouldGuardNavigation) {
      window.customNavigate = undefined;
      return;
    }

    window.customNavigate = (targetPath: string | number) => {
      setPendingTargetUrl(String(targetPath));
      setShowUnsavedModal(true);
    };

    const handleGlobalClick = (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement).closest("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (
        !href ||
        href.startsWith("#") ||
        href.startsWith("mailto:") ||
        href.startsWith("http") ||
        anchor.getAttribute("download") ||
        anchor.getAttribute("target") === "_blank"
      ) {
        return;
      }

      if (href === location.pathname) return;

      event.preventDefault();
      event.stopPropagation();
      setPendingTargetUrl(href);
      setShowUnsavedModal(true);
    };

    const handlePopState = () => {
      setPendingTargetUrl("-1");
      setShowUnsavedModal(true);
      window.history.pushState(null, "", window.location.pathname);
    };

    window.history.pushState(null, "", window.location.pathname);
    document.addEventListener("click", handleGlobalClick, true);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.customNavigate = undefined;
      document.removeEventListener("click", handleGlobalClick, true);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [shouldGuardNavigation, location.pathname]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (shouldGuardNavigation) {
        event.preventDefault();
        event.returnValue = "Changes you made may not be saved.";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [shouldGuardNavigation]);

  const navigateToPending = (target: string | null) => {
    allowNavigationRef.current = true;
    setShowUnsavedModal(false);
    setHasUnsavedChanges(false);
    setSavingAndLeaving(false);
    window.customNavigate = undefined;
    setPendingTargetUrl(null);

    if (target === "-1") {
      navigate(-1);
    } else if (target) {
      navigate(target);
    }
  };

  const confirmLeave = () => {
    if (savingAndLeaving) return;
    navigateToPending(pendingTargetUrl);
  };

  const saveAndContinue = async () => {
    if (savingAndLeaving) return;

    const target = pendingTargetUrl;
    setSavingAndLeaving(true);

    try {
      const saved = await runSaveHandler();
      if (!saved) {
        setSavingAndLeaving(false);
        return;
      }
      navigateToPending(target);
    } catch {
      setSavingAndLeaving(false);
    }
  };

  // Only block the outlet on initial auth load, not on background user refreshes
  if (!initialized || (isLoading && !user)) return null;

  // User not authenticated
  if (!isAuth) {
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }

  // Role check
  if (!user || !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Smile Portal access is gated on verifySmile only (independent of the
  // Cases Portal `verified` flag). Only Super Admin can bypass it.
  if (user.role !== "Super Admin" && !user.verifySmile) {
    return <Navigate to="/dashboard/profile" replace />;
  }

  // Restrict allocation routes
  if (
    (user.role === "User" || user.role === "Side Admin") &&
    location.pathname.startsWith("/dashboard/allocation")
  ) {
    return <Navigate to="/dashboard/profile" replace />;
  }

  return (
    <>
      <Outlet />

      {showCreditModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex justify-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-yellow-100">
                <span className="text-5xl">⚠️</span>
              </div>
            </div>

            <h2 className="mt-5 text-center text-2xl font-bold text-gray-900">
              No Smile Credits Remaining
            </h2>

            <p className="mt-3 text-center text-gray-600">
              You have used all of your Smile credits. Purchase more credits to
              continue generating smile designs.
            </p>

            <div className="mt-6 flex flex-col gap-3">
              <button
                onClick={() => {
                  setShowCreditModal(false);
                  navigate("/dashboard/smiledesign/list-credits");
                }}
                className="w-full rounded-lg bg-[#2fa9bf] px-4 py-3 font-semibold text-white transition hover:bg-[#258ca0]"
              >
                Buy Credits
              </button>
              <button
                onClick={() => {
                  setShowCreditModal(false);
                  navigate("/dashboard/smiledesign/my-smiles", {
                    replace: true,
                  });
                }}
                className="w-full rounded-lg border border-gray-300 px-4 py-3 font-semibold text-gray-700 transition hover:bg-gray-50"
              >
                Go to My Smiles
              </button>
            </div>
          </div>
        </div>
      )}

      {showUnsavedModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
          <div className="w-full max-w-md bg-[#242424] border border-white/10 rounded-2xl p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-2">Unsaved Changes</h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              You have unsaved smile modifications or generated history attached to this case.
              Save before leaving, or discard to leave without saving.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={confirmLeave}
                disabled={savingAndLeaving}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-500 rounded-xl transition shadow-lg shadow-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Discard & Leave
              </button>
              <button
                onClick={saveAndContinue}
                disabled={savingAndLeaving}
                className="px-4 py-2 text-sm font-medium text-white bg-[#2fa9bf] hover:bg-[#36bfd8] rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingAndLeaving ? "Saving..." : "Save and Continue"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function SmileCheckAuth(props: Props) {
  return (
    <SmileUnsavedChangesProvider>
      <SmileCheckAuthContent {...props} />
    </SmileUnsavedChangesProvider>
  );
}
